/**
 * Livret scolaire PDF generator — BTS NDRC (reproduction fidèle du modèle officiel 2026)
 *
 * Reproduit le livret scolaire officiel BTS 2026 « toutes spécialités » :
 *   - Page 1 (A4 paysage) : entête administratif (Examen / Spécialité / Année /
 *     Nom / Prénom / Cachet établissement ; DDN / Lieu / INE / Langue vivante)
 *     + tableau principal « Classe de 1ère année » (T1, T2, T3, Moyenne) /
 *     « Enseignements » / « Classe de 2ème année » (T1, T2, T3, Moyenne) /
 *     « Appréciations »
 *     + ligne PIX + bas de page (avis conseil de classe + répartition des avis
 *     en % + sessions précédentes + signature chef d'établissement)
 *   - Page 2 (A4 paysage) : graphique « Profil du candidat en rouge »
 *     (axe Y de -10 à +10, axe X = 10 matières en vertical) + 2 cadres
 *     « Visa du Président du jury »
 *
 * Source des données : identique au générateur Excel (grades, appreciations,
 * conseil, config, student). Les moyennes Y1/Y2 sont recalculées côté client.
 */

import { jsPDF } from 'jspdf';
import { supabase } from './supabase';
import {
  AppreciationPeriod,
  AVIS_LABEL,
  AvisConseil,
  SessionPrecedente,
  fetchAppreciations,
  fetchConseil,
  fetchGrades,
  fetchLivretConfig,
  fetchStudent,
  LivretGenerationError,
  Student,
  aggregateAppreciations,
  aggregateGrades,
  sanitizeFilename,
} from './livretGenerator';

// ============================================================================
// Référentiel des matières BTS NDRC (ordre EXACT du livret officiel)
// ============================================================================

interface SubjectDef {
  label: string;
  /** subject_id dans la DB (si null = ligne vide dans le livret — réservée aux spécialités alternatives) */
  subjectId: string | null;
}

const NDRC_SUBJECTS: SubjectDef[] = [
  { label: 'Culture générale et expression', subjectId: '6dc63e69-ff06-46a6-b29a-5de059975399' },
  { label: 'Langue vivante étrangère 1', subjectId: 'eea64ab6-4952-46ad-bc4f-32f641435747' },
  { label: 'Culture économique, juridique et managériale', subjectId: 'b6d9290b-5bb3-4a87-9814-5b714eab15d3' },
  { label: 'Culture économique, juridique et managériale appliquée', subjectId: null },
  { label: 'Relation client et négociation vente', subjectId: '941bbb39-40b3-4f74-a81f-cc968a528bb0' },
  { label: 'Relation client à distance et digitalisation', subjectId: '4912d33e-f28c-4ab6-9438-d3d7ffd6f259' },
  { label: 'Relation client et animation de réseaux', subjectId: 'a7b8cac9-cd3b-4a6b-9ccc-8fe51acaff31' },
  { label: 'Atelier de professionnalisation', subjectId: null },
  { label: 'Enseignement facultatif : langue vivante 2', subjectId: null },
  { label: 'Épreuve facultative engagement étudiant', subjectId: null },
];

/** Nombre de lignes de matières à afficher (matières officielles + lignes vides pour extensions) */
const TOTAL_SUBJECT_ROWS = 16;

// ============================================================================
// Layout (A4 paysage : 842 × 595 pt)
// ============================================================================

const PAGE_W = 842;
const PAGE_H = 595;
const MARGIN_X = 20;
const MARGIN_TOP = 15;

const COLORS = {
  black: [0, 0, 0] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  gray: [100, 100, 100] as [number, number, number],
  lightGray: [240, 240, 240] as [number, number, number],
  pink: [255, 220, 225] as [number, number, number], // fond salmon/pink du bloc "Avis du conseil"
  red: [220, 30, 30] as [number, number, number],
};

// ============================================================================
// Helpers de dessin bas-niveau
// ============================================================================

type Align = 'left' | 'center' | 'right';

interface TextOpts {
  font?: 'helvetica' | 'times';
  style?: 'normal' | 'bold' | 'italic' | 'bolditalic';
  size?: number;
  align?: Align;
  color?: [number, number, number];
  /** angle en degrés (0 = horizontal) */
  angle?: number;
  /** largeur max pour le wrap (sinon pas de wrap) */
  maxWidth?: number;
  /** interligne en pts */
  lineHeight?: number;
}

function setTextStyle(doc: jsPDF, opts: TextOpts): void {
  doc.setFont(opts.font ?? 'helvetica', opts.style ?? 'normal');
  doc.setFontSize(opts.size ?? 8);
  const c = opts.color ?? COLORS.black;
  doc.setTextColor(c[0], c[1], c[2]);
}

function drawRect(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  opts: { fill?: [number, number, number]; stroke?: [number, number, number]; lineWidth?: number } = {}
): void {
  const stroke = opts.stroke ?? COLORS.black;
  doc.setDrawColor(stroke[0], stroke[1], stroke[2]);
  doc.setLineWidth(opts.lineWidth ?? 0.3);
  if (opts.fill) {
    doc.setFillColor(opts.fill[0], opts.fill[1], opts.fill[2]);
    doc.rect(x, y, w, h, 'FD');
  } else {
    doc.rect(x, y, w, h, 'S');
  }
}

/** Dessine du texte dans une cellule rectangulaire avec alignement (horizontal + vertical centré). */
function drawCellText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  w: number,
  h: number,
  opts: TextOpts = {}
): void {
  if (!text) return;
  setTextStyle(doc, opts);
  const align = opts.align ?? 'center';
  const padding = 2;
  let tx = x + w / 2;
  if (align === 'left') tx = x + padding;
  else if (align === 'right') tx = x + w - padding;

  // Wrap si maxWidth ou si texte trop long
  const maxW = opts.maxWidth ?? w - padding * 2;
  const lines = doc.splitTextToSize(text, maxW) as string[];
  const lineHeight = opts.lineHeight ?? (opts.size ?? 8) * 1.15;
  const totalH = lines.length * lineHeight;
  // Vertical center
  const startY = y + (h - totalH) / 2 + (opts.size ?? 8) * 0.85;

  lines.forEach((line, i) => {
    doc.text(line, tx, startY + i * lineHeight, { align, angle: opts.angle });
  });
}

function drawLine(
  doc: jsPDF,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: [number, number, number] = COLORS.black,
  lineWidth = 0.3
): void {
  doc.setDrawColor(color[0], color[1], color[2]);
  doc.setLineWidth(lineWidth);
  doc.line(x1, y1, x2, y2);
}

/** Dessine un "X" diagonal dans une cellule (pour les colonnes "Moyenne" en entête). */
function drawDiagonalCross(doc: jsPDF, x: number, y: number, w: number, h: number): void {
  drawLine(doc, x, y, x + w, y + h, COLORS.black, 0.4);
}

function fmtGrade(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '';
  return n.toFixed(2).replace('.', ',');
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR');
}

function fmtDateObj(d: Date): string {
  return d.toLocaleDateString('fr-FR');
}

/**
 * Télécharge le cachet depuis le bucket `livret-templates` et retourne
 * une data URL base64 prête à insérer dans un PDF via `doc.addImage()`.
 * Renvoie null si le chemin est vide, ou si le téléchargement échoue.
 */
async function fetchCachetDataUrl(path: string | null | undefined): Promise<string | null> {
  if (!path) return null;
  try {
    const { data, error } = await supabase.storage.from('livret-templates').download(path);
    if (error || !data) return null;
    const arrBuf = await data.arrayBuffer();
    // Détection simple du mime type via signature
    const bytes = new Uint8Array(arrBuf);
    let mime = 'image/png';
    if (bytes[0] === 0xff && bytes[1] === 0xd8) mime = 'image/jpeg';
    // Base64 via Blob → FileReader, on évite btoa pour gros fichiers
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          // FileReader peut renvoyer data:application/octet-stream → on force le mime
          const comma = result.indexOf(',');
          const b64 = comma >= 0 ? result.substring(comma + 1) : result;
          resolve(`data:${mime};base64,${b64}`);
        } else {
          reject(new Error('FileReader unexpected type'));
        }
      };
      reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
      reader.readAsDataURL(new Blob([arrBuf], { type: mime }));
    });
  } catch (e) {
    console.warn('fetchCachetDataUrl failed:', e);
    return null;
  }
}

/**
 * Dessine un cachet (image) + date de génération dans une zone donnée.
 * Le cachet est centré horizontalement, la date en-dessous en petits caractères.
 */
function drawCachet(
  doc: jsPDF,
  cachetDataUrl: string | null,
  generationDate: Date,
  x: number,
  y: number,
  w: number,
  h: number
): void {
  const dateStr = fmtDateObj(generationDate);
  const dateH = 10; // zone réservée à la date en bas
  const padding = 3;
  const imgW = Math.min(w - 2 * padding, 90);
  const imgH = Math.min(h - dateH - 2 * padding, 55);

  if (cachetDataUrl) {
    try {
      const imgX = x + (w - imgW) / 2;
      const imgY = y + padding;
      const format = cachetDataUrl.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
      doc.addImage(cachetDataUrl, format, imgX, imgY, imgW, imgH, undefined, 'FAST');
    } catch (e) {
      console.warn('drawCachet addImage failed:', e);
    }
  }
  // Date en bas
  drawCellText(doc, dateStr, x, y + h - dateH, w, dateH, {
    size: 7,
    style: 'italic',
    align: 'center',
  });
}

function avisDistribution(avisList: (AvisConseil | null | undefined)[]): Record<AvisConseil | 'none', number> {
  const counts: Record<AvisConseil | 'none', number> = {
    tres_favorable: 0,
    favorable: 0,
    reserve: 0,
    defavorable: 0,
    none: 0,
  };
  for (const a of avisList) {
    if (a) counts[a] += 1;
    else counts.none += 1;
  }
  return counts;
}

// ============================================================================
// Page 1 — Entête administratif
// ============================================================================

interface HeaderData {
  specialite: string;
  session: number;
  nom: string;
  prenom: string;
  dateNaissance: string | null;
  lieuNaissance: string | null;
  ine: string | null;
  langueVivante: string | null;
  etablissement: string;
  cachetDataUrl: string | null;
  generationDate: Date;
}

function drawHeaderBlock(doc: jsPDF, data: HeaderData): number {
  const x0 = MARGIN_X;
  const y0 = MARGIN_TOP;
  const W = PAGE_W - 2 * MARGIN_X;
  const H = 58;

  // Bordure externe
  drawRect(doc, x0, y0, W, H);

  // Découpage horizontal des colonnes
  // [Examen label | BTS/Spécialité | Année/2026 | Nom/DDN-Lieu | Prénom/INE-LV | Cachet]
  const c1 = 55; // Examen / Spécialité label
  const c2 = 170; // BTS value / Spécialité value
  const c3 = 65; // Année label / Année value
  const c4 = 180; // Nom / DDN+Lieu
  const c5 = 180; // Prénom / INE+LV
  const c6 = W - (c1 + c2 + c3 + c4 + c5); // Cachet

  const x1 = x0 + c1;
  const x2 = x1 + c2;
  const x3 = x2 + c3;
  const x4 = x3 + c4;
  const x5 = x4 + c5;

  // Split horizontal : première rangée 30pt, deuxième 28pt
  const rowH1 = 24;
  const yMid = y0 + rowH1;

  // Bordures verticales principales
  drawLine(doc, x1, y0, x1, y0 + H);
  drawLine(doc, x2, y0, x2, y0 + H);
  drawLine(doc, x3, y0, x3, y0 + H);
  drawLine(doc, x4, y0, x4, y0 + H);
  drawLine(doc, x5, y0, x5, y0 + H);

  // Bordure horizontale intermédiaire (sauf colonne Cachet)
  drawLine(doc, x0, yMid, x5, yMid);

  // Ligne 1 - labels gauche + values
  drawCellText(doc, 'Examen :', x0, y0, c1, rowH1, { size: 7, align: 'right' });
  drawCellText(doc, 'BTS', x1, y0, c2, rowH1, { size: 11, style: 'bold' });
  drawCellText(doc, "Année de\nl'examen", x2, y0, c3, rowH1, { size: 6, lineHeight: 7 });
  drawCellText(doc, 'Nom :', x3, y0, c4, rowH1, { size: 7, align: 'left' });
  drawCellText(doc, data.nom.toUpperCase(), x3 + 30, y0, c4 - 30, rowH1, { size: 10, style: 'bold', align: 'left' });
  drawCellText(doc, 'Prénom :', x4, y0, c5, rowH1, { size: 7, align: 'left' });
  drawCellText(doc, data.prenom, x4 + 36, y0, c5 - 36, rowH1, { size: 10, style: 'bold', align: 'left' });
  drawCellText(
    doc,
    "Cachet de l'établissement, date et signature du chef d'établissement :",
    x5,
    y0,
    c6,
    10,
    { size: 5.5, align: 'left' }
  );

  // Ligne 2 - Spécialité + 2026 + DDN/Lieu + INE/LV
  const rowH2 = H - rowH1;
  drawCellText(doc, 'Spécialité :', x0, yMid, c1, rowH2, { size: 7, align: 'right' });
  drawCellText(doc, data.specialite, x1, yMid, c2, rowH2, {
    size: 8.5,
    style: 'bold',
    lineHeight: 10,
  });
  drawCellText(doc, String(data.session), x2, yMid, c3, rowH2, { size: 14, style: 'bold' });

  // DDN et Lieu empilés dans la colonne Nom
  const halfR = rowH2 / 2;
  drawLine(doc, x3, yMid + halfR, x4, yMid + halfR);
  drawCellText(doc, 'Date de naissance:', x3, yMid, 56, halfR, { size: 6, align: 'left' });
  drawCellText(doc, fmtDate(data.dateNaissance), x3 + 56, yMid, c4 - 56, halfR, {
    size: 8,
    align: 'left',
  });
  drawCellText(doc, 'Lieu de\nnaissance :', x3, yMid + halfR, 56, halfR, {
    size: 6,
    align: 'left',
    lineHeight: 7,
  });
  drawCellText(doc, data.lieuNaissance ?? '', x3 + 56, yMid + halfR, c4 - 56, halfR, {
    size: 8,
    align: 'left',
  });

  // INE + Langue vivante (colonne Prénom en ligne 2) — split vertical 50/50
  const cINE = 80;
  const cLVlabel = 55;
  drawLine(doc, x4 + cINE, yMid, x4 + cINE, yMid + rowH2);
  drawLine(doc, x4 + cINE + cLVlabel, yMid, x4 + cINE + cLVlabel, yMid + rowH2);
  drawCellText(doc, 'INE :', x4, yMid, 26, rowH2, { size: 6, align: 'left' });
  drawCellText(doc, data.ine ?? '', x4 + 26, yMid, cINE - 26, rowH2, { size: 8, align: 'left' });
  drawCellText(doc, 'Langue vivante', x4 + cINE, yMid, cLVlabel, rowH2, {
    size: 6,
    align: 'center',
  });
  drawCellText(doc, data.langueVivante ?? '', x4 + cINE + cLVlabel, yMid, c5 - cINE - cLVlabel, rowH2, {
    size: 8,
    style: 'bold',
  });

  // Cachet — image (si présente) + date de génération, sinon texte établissement en fallback
  if (data.cachetDataUrl) {
    drawCachet(doc, data.cachetDataUrl, data.generationDate, x5, yMid, c6, rowH2);
  } else {
    drawCellText(doc, data.etablissement, x5, yMid, c6, rowH2, {
      size: 10,
      style: 'bold',
      align: 'center',
    });
    drawCellText(doc, fmtDateObj(data.generationDate), x5, yMid + rowH2 - 10, c6, 10, {
      size: 7,
      style: 'italic',
      align: 'center',
    });
  }

  return y0 + H; // y de fin du bloc entête
}

// ============================================================================
// Page 1 — Tableau principal (notes + appréciations)
// ============================================================================

interface TableData {
  subjects: {
    label: string;
    y1t1: number | null;
    y1t2: number | null;
    y1t3: number | null;
    moyY1: number | null;
    y2t1: number | null;
    y2t2: number | null;
    y2t3: number | null;
    moyY2: number | null;
    appreciation: string;
  }[];
  sessionPrev: number; // année précédente (ex: 2025)
  sessionCur: number; // année examen (ex: 2026)
}

function drawGradesTable(doc: jsPDF, yStart: number, data: TableData): number {
  const x0 = MARGIN_X;
  const W = PAGE_W - 2 * MARGIN_X;

  // Largeurs colonnes
  const tCol = 28; // chaque colonne trimestre
  const moyCol = 30; // colonne Moyenne
  const y1Block = tCol * 3 + moyCol; // 3 trimestres + moyenne
  const y2Block = tCol * 3 + moyCol;
  const ensW = 200;
  const apprW = W - y1Block - ensW - y2Block;

  // Entête tableau — 3 sous-lignes : année, label trimestre, détail semestre
  const hTitle = 16; // "Classe de 1ère année (1) | Enseignements | Classe de 2ème année (2) | Appréciations"
  const hSubH = 30; // "T1/T2/T3/Moyenne | ... | T1/T2/T3/Moyenne"
  const headerH = hTitle + hSubH;

  // Niveau 1 — bandeaux de titre
  const yTitle = yStart;
  drawRect(doc, x0, yTitle, y1Block, hTitle);
  drawRect(doc, x0 + y1Block, yTitle, ensW, hTitle + hSubH);
  drawRect(doc, x0 + y1Block + ensW, yTitle, y2Block, hTitle);
  drawRect(doc, x0 + y1Block + ensW + y2Block, yTitle, apprW, hTitle + hSubH);

  // Titre 1ère année — split horizontal : "Classe de 1ère année (1)" | "2025"
  const tAnneeLabel = y1Block - 30;
  drawLine(doc, x0 + tAnneeLabel, yTitle, x0 + tAnneeLabel, yTitle + hTitle);
  drawCellText(doc, 'Classe de\n1ère année (1)', x0, yTitle, tAnneeLabel, hTitle, {
    size: 7,
    style: 'bold',
    lineHeight: 7.5,
  });
  drawCellText(doc, String(data.sessionPrev), x0 + tAnneeLabel, yTitle, 30, hTitle, {
    size: 9,
    style: 'bold',
  });

  // Titre Enseignements
  drawCellText(doc, 'Enseignements', x0 + y1Block, yTitle, ensW, hTitle, {
    size: 10,
    style: 'bold',
  });

  // Titre 2ème année — split "Classe de 2ème année (2)" | "2026"
  const x2Start = x0 + y1Block + ensW;
  drawLine(doc, x2Start + tAnneeLabel, yTitle, x2Start + tAnneeLabel, yTitle + hTitle);
  drawCellText(doc, 'Classe de\n2ème année (2)', x2Start, yTitle, tAnneeLabel, hTitle, {
    size: 7,
    style: 'bold',
    lineHeight: 7.5,
  });
  drawCellText(doc, String(data.sessionCur), x2Start + tAnneeLabel, yTitle, 30, hTitle, {
    size: 9,
    style: 'bold',
  });

  // Titre Appréciations
  drawCellText(
    doc,
    'Appréciations',
    x0 + y1Block + ensW + y2Block,
    yTitle,
    apprW,
    hTitle,
    { size: 10, style: 'bold' }
  );

  // Niveau 2 — sous-colonnes T1/T2/T3/Moy
  const ySub = yTitle + hTitle;
  const subCols = ['1er\ntrimestre\n1er\nsemestre', '2ème\ntrimestre\n2ème\nsemestre', '3ème\ntrimestre', ''];
  for (let i = 0; i < 3; i++) {
    const cx = x0 + i * tCol;
    drawRect(doc, cx, ySub, tCol, hSubH);
    drawCellText(doc, subCols[i], cx, ySub, tCol, hSubH, { size: 5.5, lineHeight: 6 });
  }
  // Moyenne Y1
  const moyX1 = x0 + 3 * tCol;
  drawRect(doc, moyX1, ySub, moyCol, hSubH);
  drawCellText(doc, 'Moyenne', moyX1, ySub, moyCol, hSubH, { size: 6.5 });
  drawDiagonalCross(doc, moyX1, ySub + hSubH - 8, moyCol, -hSubH + 10);

  // Sous-colonnes Y2
  for (let i = 0; i < 3; i++) {
    const cx = x2Start + i * tCol;
    drawRect(doc, cx, ySub, tCol, hSubH);
    drawCellText(doc, subCols[i], cx, ySub, tCol, hSubH, { size: 5.5, lineHeight: 6 });
  }
  const moyX2 = x2Start + 3 * tCol;
  drawRect(doc, moyX2, ySub, moyCol, hSubH);
  drawCellText(doc, 'Moyenne', moyX2, ySub, moyCol, hSubH, { size: 6.5 });
  drawDiagonalCross(doc, moyX2, ySub + hSubH - 8, moyCol, -hSubH + 10);

  // Corps du tableau — 16 lignes
  const rowH = 14;
  const yBody = ySub + hSubH;
  const totalRows = TOTAL_SUBJECT_ROWS;

  for (let r = 0; r < totalRows; r++) {
    const yr = yBody + r * rowH;
    const subj = data.subjects[r];

    // Colonnes Y1
    for (let i = 0; i < 3; i++) {
      drawRect(doc, x0 + i * tCol, yr, tCol, rowH);
      if (subj) {
        const val = [subj.y1t1, subj.y1t2, subj.y1t3][i];
        drawCellText(doc, fmtGrade(val), x0 + i * tCol, yr, tCol, rowH, { size: 8 });
      }
    }
    // Moy Y1
    drawRect(doc, moyX1, yr, moyCol, rowH);
    if (subj) drawCellText(doc, fmtGrade(subj.moyY1), moyX1, yr, moyCol, rowH, { size: 8, style: 'bold' });

    // Enseignement
    drawRect(doc, x0 + y1Block, yr, ensW, rowH);
    if (subj && subj.label) {
      drawCellText(doc, subj.label, x0 + y1Block + 3, yr, ensW - 6, rowH, {
        size: 7.5,
        align: 'left',
      });
    }

    // Colonnes Y2
    for (let i = 0; i < 3; i++) {
      drawRect(doc, x2Start + i * tCol, yr, tCol, rowH);
      if (subj) {
        const val = [subj.y2t1, subj.y2t2, subj.y2t3][i];
        drawCellText(doc, fmtGrade(val), x2Start + i * tCol, yr, tCol, rowH, { size: 8 });
      }
    }
    // Moy Y2
    drawRect(doc, moyX2, yr, moyCol, rowH);
    if (subj) drawCellText(doc, fmtGrade(subj.moyY2), moyX2, yr, moyCol, rowH, { size: 8, style: 'bold' });

    // Appréciation
    drawRect(doc, x0 + y1Block + ensW + y2Block, yr, apprW, rowH);
    if (subj?.appreciation) {
      drawCellText(
        doc,
        subj.appreciation,
        x0 + y1Block + ensW + y2Block + 3,
        yr,
        apprW - 6,
        rowH,
        { size: 6.5, align: 'left' }
      );
    }
  }

  return yBody + totalRows * rowH;
}

// ============================================================================
// Page 1 — Bandeau footnotes (année antérieure / année examen) + PIX
// ============================================================================

function drawFootnotesBlock(
  doc: jsPDF,
  yStart: number,
  pixCertifie: boolean
): number {
  const x0 = MARGIN_X;
  const W = PAGE_W - 2 * MARGIN_X;
  const h = 18;

  // Footnote 1 (sous le bloc Y1)
  drawCellText(doc, "(1) Année antérieure à celle de l'examen", x0 + 60, yStart, 200, h, {
    size: 6,
    style: 'italic',
    align: 'left',
  });
  // Footnote 2 (sous le bloc Y2)
  drawCellText(doc, "(2) Année de l'examen", x0 + W / 2 + 20, yStart, 150, h, {
    size: 6,
    style: 'italic',
    align: 'left',
  });

  // PIX à gauche
  const pixY = yStart + h;
  const pixH = 20;
  drawRect(doc, x0 + 60, pixY, 280, pixH);
  drawCellText(doc, 'Certification de compétences numériques PIX', x0 + 60, pixY, 230, 10, {
    size: 7,
    style: 'bold',
    align: 'left',
  });
  drawCellText(
    doc,
    "cocher la case pour attester de l'obtention de la certification",
    x0 + 60,
    pixY + 10,
    230,
    10,
    { size: 6, style: 'italic', align: 'left' }
  );
  // Checkbox
  const cbx = x0 + 310;
  const cby = pixY + 5;
  drawRect(doc, cbx, cby, 10, 10);
  if (pixCertifie) {
    drawLine(doc, cbx + 1, cby + 5, cbx + 4, cby + 9, COLORS.black, 0.8);
    drawLine(doc, cbx + 4, cby + 9, cbx + 9, cby + 1, COLORS.black, 0.8);
  }

  return pixY + pixH;
}

// ============================================================================
// Page 1 — Bas de page : Avis conseil / Répartition / Sessions précédentes / Signature
// ============================================================================

interface BottomData {
  avisLabel: string;
  observations: string;
  repartition: { tresFav: string; fav: string; doitFaire: string; effectif: string };
  sessionsPrecedentes: { annee: number; presentes: string; recus: string; pct: string }[];
  cachetDataUrl: string | null;
  generationDate: Date;
}

function drawBottomBlock(doc: jsPDF, yStart: number, data: BottomData): void {
  const x0 = MARGIN_X;
  const W = PAGE_W - 2 * MARGIN_X;
  const h = 90;

  // Découpage horizontal :
  // Avis conseil (obs) | Répartition des avis | Sessions précédentes | Date/Signature
  const cAvis = 300;
  const cRep = 140;
  const cSess = 160;
  const cSign = W - cAvis - cRep - cSess;

  const xR = x0 + cAvis;
  const xS = xR + cRep;
  const xD = xS + cSess;

  // Header row (petite hauteur)
  const headerH = 14;
  drawRect(doc, x0, yStart, cAvis, headerH);
  drawRect(doc, xR, yStart, cRep, headerH);
  drawRect(doc, xS, yStart, cSess, headerH);
  drawRect(doc, xD, yStart, cSign, headerH);

  drawCellText(doc, 'AVIS DU CONSEIL DE CLASSE ET OBSERVATIONS EVENTUELLES', x0, yStart, cAvis, headerH, {
    size: 6.5,
    style: 'bold',
  });
  drawCellText(doc, 'REPARTITION DES AVIS EN %', xR, yStart, cRep, headerH, {
    size: 6.5,
    style: 'bold',
  });
  drawCellText(doc, 'Résultats des sessions précédentes', xS, yStart, cSess, headerH, {
    size: 6.5,
    style: 'bold',
  });
  drawCellText(doc, 'DATE, SIGNATURE ET REMARQUES EVENTUELLES', xD, yStart, cSign, headerH, {
    size: 6.5,
    style: 'bold',
  });

  // Corps
  const bodyH = h - headerH;
  const yB = yStart + headerH;
  // Avis conseil : première bande rose avec l'avis
  drawRect(doc, x0, yB, cAvis, 16, { fill: COLORS.pink });
  drawCellText(doc, data.avisLabel, x0, yB, cAvis, 16, { size: 9, style: 'bold' });
  // En dessous : observations (bloc libre)
  drawRect(doc, x0, yB + 16, cAvis, bodyH - 16);
  drawCellText(doc, data.observations, x0 + 4, yB + 16, cAvis - 8, bodyH - 16, {
    size: 7,
    align: 'left',
    lineHeight: 8,
  });

  // Répartition des avis — tableau 4 colonnes
  drawRect(doc, xR, yB, cRep, bodyH);
  const rSubH = 30; // entêtes colonnes "Avis" + sous-labels
  const rValH = 20;
  const rEffH = bodyH - rSubH - rValH;

  drawCellText(doc, 'Avis', xR, yB, cRep * 0.75, 10, { size: 6.5, style: 'italic' });

  const cRep4 = cRep / 4;
  const labels = ['Très\nFavorable', 'Favorable', 'Doit faire\nses\npreuves', 'Effectif total de la\nclasse'];
  for (let i = 0; i < 4; i++) {
    const cx = xR + i * cRep4;
    drawLine(doc, cx, yB, cx, yB + bodyH);
    drawCellText(doc, labels[i], cx, yB + 10, cRep4, 20, { size: 5.5, lineHeight: 6 });
  }
  drawLine(doc, xR, yB + rSubH, xR + cRep, yB + rSubH);

  // Valeurs
  const vals = [data.repartition.tresFav, data.repartition.fav, data.repartition.doitFaire, data.repartition.effectif];
  for (let i = 0; i < 4; i++) {
    drawCellText(doc, vals[i], xR + i * cRep4, yB + rSubH, cRep4, rValH + rEffH, {
      size: 9,
      style: 'bold',
    });
  }

  // Sessions précédentes — 4 colonnes × 3 lignes
  drawRect(doc, xS, yB, cSess, bodyH);
  const sessHdrH = 12;
  drawLine(doc, xS, yB + sessHdrH, xS + cSess, yB + sessHdrH);
  const sCols = ['Année', 'Présentés', 'Recues', '%'];
  const sColW = cSess / 4;
  for (let i = 0; i < 4; i++) {
    const cx = xS + i * sColW;
    if (i > 0) drawLine(doc, cx, yB, cx, yB + bodyH);
    drawCellText(doc, sCols[i], cx, yB, sColW, sessHdrH, { size: 6 });
  }
  const sRowH = (bodyH - sessHdrH) / 3;
  for (let r = 0; r < 3; r++) {
    const yr = yB + sessHdrH + r * sRowH;
    if (r > 0) drawLine(doc, xS, yr, xS + cSess, yr);
    const row = data.sessionsPrecedentes[r];
    if (row) {
      drawCellText(doc, String(row.annee), xS, yr, sColW, sRowH, { size: 7.5 });
      drawCellText(doc, row.presentes, xS + sColW, yr, sColW, sRowH, { size: 7.5 });
      drawCellText(doc, row.recus, xS + 2 * sColW, yr, sColW, sRowH, { size: 7.5 });
      drawCellText(doc, row.pct, xS + 3 * sColW, yr, sColW, sRowH, { size: 7.5 });
    }
  }

  // Date/Signature — cachet + date de génération
  drawRect(doc, xD, yB, cSign, bodyH);
  drawCachet(doc, data.cachetDataUrl, data.generationDate, xD, yB, cSign, bodyH);
}

// ============================================================================
// Page 2 — Graphique « Profil du candidat en rouge » + Signatures
// ============================================================================

interface ChartData {
  /** Une entrée par matière dans l'ordre du livret. Valeur = (moyenne Y2 ou Y1) - 10, null si absente. */
  points: (number | null)[];
  labels: string[];
}

function drawProfileChart(doc: jsPDF, data: ChartData): void {
  const x0 = MARGIN_X + 35;
  const y0 = MARGIN_TOP + 40;
  const W = PAGE_W - 2 * MARGIN_X - 70;
  const H = 420;

  // Bordure externe
  drawRect(doc, MARGIN_X, MARGIN_TOP, PAGE_W - 2 * MARGIN_X, PAGE_H - MARGIN_TOP - 110);

  // Zone graphique
  const plotX = x0;
  const plotY = y0 + 80; // laisse 80pt pour les labels X verticaux au-dessus
  const plotW = W;
  const plotH = H - 100; // laisse 20pt pour le label "Profil du candidat en rouge" en bas

  // Axe Y : de -10 à +10, graduations tous les 2
  const yTicks = [10, 8, 6, 4, 2, 0, -2, -4, -6, -8, -10];
  const tickSpacing = plotH / (yTicks.length - 1);

  // Dessin lignes horizontales + labels Y
  for (let i = 0; i < yTicks.length; i++) {
    const y = plotY + i * tickSpacing;
    const tick = yTicks[i];
    drawLine(doc, plotX, y, plotX + plotW, y, tick === 0 ? COLORS.black : COLORS.gray, tick === 0 ? 0.8 : 0.15);
    const isNeg = tick < 0;
    drawCellText(
      doc,
      tick.toFixed(2).replace('.', ','),
      plotX - 30,
      y - 5,
      26,
      10,
      { size: 7, align: 'right', color: isNeg ? COLORS.red : COLORS.black }
    );
  }

  // Bordures haut / bas / gauche / droite du plot
  drawLine(doc, plotX, plotY, plotX, plotY + plotH);
  drawLine(doc, plotX + plotW, plotY, plotX + plotW, plotY + plotH);
  drawLine(doc, plotX, plotY, plotX + plotW, plotY);
  drawLine(doc, plotX, plotY + plotH, plotX + plotW, plotY + plotH);

  // Axe X : 10 catégories (NDRC_SUBJECTS), labels verticaux au-dessus de la zone
  const n = data.labels.length;
  // Les catégories sont espacées dans la zone utile (avec un peu de marge gauche/droite)
  const catMargin = plotW / n / 2;
  const catSpan = plotW - 2 * catMargin;
  const catStep = n > 1 ? catSpan / (n - 1) : 0;

  setTextStyle(doc, { size: 6.5 });
  for (let i = 0; i < n; i++) {
    const cx = plotX + catMargin + i * catStep;
    // Label vertical — on pivote le texte de -90°
    const label = data.labels[i];
    doc.text(label, cx + 2, plotY - 4, { angle: 90, align: 'left', maxWidth: 75 });
  }

  // Convertir valeur → y pixel
  const valToY = (v: number) => plotY + ((10 - v) / 20) * plotH;

  // Tracer la ligne rouge du candidat
  doc.setDrawColor(COLORS.red[0], COLORS.red[1], COLORS.red[2]);
  doc.setLineWidth(1.3);
  let prev: { x: number; y: number } | null = null;
  for (let i = 0; i < n; i++) {
    const v = data.points[i];
    if (v === null || v === undefined || Number.isNaN(v)) {
      prev = null;
      continue;
    }
    const cx = plotX + catMargin + i * catStep;
    const cy = valToY(v);
    if (prev) {
      doc.line(prev.x, prev.y, cx, cy);
    }
    // Marqueur (petit carré rouge plein)
    doc.setFillColor(COLORS.red[0], COLORS.red[1], COLORS.red[2]);
    doc.rect(cx - 1.5, cy - 1.5, 3, 3, 'F');
    prev = { x: cx, y: cy };
  }

  // Légende "Profil du candidat en rouge" sous le graphique
  const legY = plotY + plotH + 6;
  doc.setDrawColor(COLORS.red[0], COLORS.red[1], COLORS.red[2]);
  doc.setLineWidth(1.2);
  doc.line(plotX + plotW / 2 - 20, legY + 3, plotX + plotW / 2 - 6, legY + 3);
  doc.setFillColor(COLORS.red[0], COLORS.red[1], COLORS.red[2]);
  doc.rect(plotX + plotW / 2 - 14, legY + 1.5, 3, 3, 'F');
  drawCellText(doc, 'Profil du candidat en rouge', plotX + plotW / 2, legY, 200, 10, {
    size: 7,
    color: COLORS.black,
    align: 'left',
  });

  // Cadres de signature en bas
  const sigY = PAGE_H - 95;
  const sigH = 55;
  const sigW = (PAGE_W - 2 * MARGIN_X) / 3;
  const sigGap = 40;
  const sig1X = MARGIN_X + sigGap;
  const sig2X = PAGE_W - MARGIN_X - sigGap - sigW;

  drawRect(doc, sig1X, sigY, sigW, sigH);
  drawCellText(doc, 'Visa du Président du jury (groupe 1)', sig1X, sigY + 2, sigW, 10, {
    size: 7,
    style: 'bold',
  });
  drawRect(doc, sig2X, sigY, sigW, sigH);
  drawCellText(doc, 'Visa du Président du jury (groupe 2)', sig2X, sigY + 2, sigW, 10, {
    size: 7,
    style: 'bold',
  });
}

// ============================================================================
// Assemblage : collecte + mapping + rendu
// ============================================================================

interface ClassStats {
  effectif: number;
  tresFav: number;
  fav: number;
  doitFaire: number;
}

async function computeClassStats(classId: string | null, session: number): Promise<ClassStats> {
  if (!classId) return { effectif: 0, tresFav: 0, fav: 0, doitFaire: 0 };
  // Effectif = étudiants de la classe avec numero_candidat
  const { data: students } = await supabase
    .from('students')
    .select('id')
    .eq('class_id', classId)
    .not('numero_candidat', 'is', null);
  const studentIds = (students ?? []).map((s) => s.id);
  if (studentIds.length === 0) return { effectif: 0, tresFav: 0, fav: 0, doitFaire: 0 };

  const { data: conseils } = await supabase
    .from('livret_conseil_classe')
    .select('student_id, avis')
    .in('student_id', studentIds)
    .eq('session_year', session);

  const dist = avisDistribution((conseils ?? []).map((r) => r.avis as AvisConseil | null));
  return {
    effectif: studentIds.length,
    tresFav: dist.tres_favorable,
    fav: dist.favorable,
    // "Doit faire ses preuves" = réservé + défavorable
    doitFaire: dist.reserve + dist.defavorable,
  };
}

function pct(n: number, total: number): string {
  if (!total) return '';
  return `${Math.round((n / total) * 100)}%`;
}

/**
 * Génère le livret scolaire d'un étudiant et retourne un Blob PDF.
 */
export async function generateLivretPdf(
  studentId: string
): Promise<{ blob: Blob; filename: string }> {
  const student: Student = await fetchStudent(studentId);
  if (!student.numero_candidat) throw new LivretGenerationError({ code: 'missing_numero_candidat' });
  if (!student.session_examen) throw new LivretGenerationError({ code: 'missing_session_examen' });

  const btsCode = 'NDRC';
  const [config, grades, apprs, conseil, classStats] = await Promise.all([
    fetchLivretConfig(btsCode, student.session_examen),
    fetchGrades(studentId),
    fetchAppreciations(studentId),
    fetchConseil(studentId, student.session_examen),
    computeClassStats(student.class_id, student.session_examen),
  ]);

  // Fetch cachet image (best effort — un livret sans cachet reste valide)
  const cachetDataUrl = await fetchCachetDataUrl(config.cachet_storage_path);
  const generationDate = new Date();

  const aggregated = aggregateGrades(grades, student.session_examen);
  const apprBySubject = aggregateAppreciations(apprs);

  // Construction des lignes par matière
  const moyOf = (map: Map<AppreciationPeriod, number> | undefined, periods: AppreciationPeriod[]): number | null => {
    if (!map) return null;
    const vals = periods
      .map((p) => map.get(p))
      .filter((v): v is number => v !== undefined && v !== null);
    if (vals.length === 0) return null;
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100;
  };

  const subjectRows = NDRC_SUBJECTS.map((s) => {
    const m = s.subjectId ? aggregated.get(s.subjectId) : undefined;
    const y1t1 = m?.get('Y1T1') ?? null;
    const y1t2 = m?.get('Y1T2') ?? null;
    const y1t3 = m?.get('Y1T3') ?? null;
    const y2t1 = m?.get('Y2T1') ?? null;
    const y2t2 = m?.get('Y2T2') ?? null;
    const y2t3 = m?.get('Y2T3') ?? null;
    return {
      label: s.label,
      y1t1,
      y1t2,
      y1t3,
      moyY1: moyOf(m, ['Y1T1', 'Y1T2', 'Y1T3']),
      y2t1,
      y2t2,
      y2t3,
      moyY2: moyOf(m, ['Y2T1', 'Y2T2', 'Y2T3']),
      appreciation: s.subjectId ? apprBySubject.get(s.subjectId) ?? '' : '',
    };
  });

  // Compléter jusqu'à TOTAL_SUBJECT_ROWS lignes (pour l'aspect visuel)
  const paddedRows = [
    ...subjectRows,
    ...Array.from({ length: Math.max(0, TOTAL_SUBJECT_ROWS - subjectRows.length) }, () => null),
  ].slice(0, TOTAL_SUBJECT_ROWS) as (typeof subjectRows[number] | null)[];

  // Séries chart : (moyenne Y2 si présente, sinon Y1) - 10
  const chartPoints: (number | null)[] = subjectRows.map((r) => {
    const m = r.moyY2 ?? r.moyY1;
    return m === null ? null : Math.round((m - 10) * 100) / 100;
  });
  const chartLabels = subjectRows.map((r) => r.label);

  // Construction du PDF
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

  // — PAGE 1 —
  const yAfterHeader = drawHeaderBlock(doc, {
    specialite: config.bts_specialite,
    session: config.session_examen,
    nom: student.last_name,
    prenom: student.first_name,
    dateNaissance: student.date_naissance,
    lieuNaissance: student.lieu_naissance,
    ine: student.ine,
    langueVivante: student.langue_vivante_1,
    etablissement: config.etablissement_nom,
    cachetDataUrl,
    generationDate,
  });

  const yAfterTable = drawGradesTable(doc, yAfterHeader + 6, {
    subjects: paddedRows.map((r) =>
      r ?? {
        label: '',
        y1t1: null,
        y1t2: null,
        y1t3: null,
        moyY1: null,
        y2t1: null,
        y2t2: null,
        y2t3: null,
        moyY2: null,
        appreciation: '',
      }
    ),
    sessionPrev: config.session_examen - 1,
    sessionCur: config.session_examen,
  });

  const yAfterFootnotes = drawFootnotesBlock(doc, yAfterTable + 2, conseil?.pix_certifie ?? false);

  const totalExamined = classStats.tresFav + classStats.fav + classStats.doitFaire;

  // Sessions précédentes : on utilise la config admin (/admin/etablissement).
  // On affiche exactement 3 lignes ; si la config en fournit moins, on complète
  // avec les 3 années précédant la session courante, valeurs à 0 par défaut.
  const defaultSessions: SessionPrecedente[] = [
    { annee: config.session_examen - 1, presentes: 0, recus: 0 },
    { annee: config.session_examen - 2, presentes: 0, recus: 0 },
    { annee: config.session_examen - 3, presentes: 0, recus: 0 },
  ];
  const configured = (config.sessions_precedentes ?? []).slice(0, 3);
  const merged: SessionPrecedente[] = [];
  for (let i = 0; i < 3; i++) {
    merged.push(configured[i] ?? defaultSessions[i]);
  }
  const sessionsDisplay = merged.map((s) => ({
    annee: s.annee,
    presentes: String(s.presentes ?? 0),
    recus: String(s.recus ?? 0),
    pct: s.presentes > 0 ? `${Math.round(((s.recus ?? 0) / s.presentes) * 100)}%` : '',
  }));

  drawBottomBlock(doc, yAfterFootnotes + 4, {
    avisLabel: conseil?.avis ? AVIS_LABEL[conseil.avis] : '',
    observations: conseil?.observations ?? '',
    repartition: {
      tresFav: pct(classStats.tresFav, totalExamined),
      fav: pct(classStats.fav, totalExamined),
      doitFaire: pct(classStats.doitFaire, totalExamined),
      effectif: classStats.effectif > 0 ? String(classStats.effectif) : '',
    },
    sessionsPrecedentes: sessionsDisplay,
    cachetDataUrl,
    generationDate,
  });

  // — PAGE 2 —
  doc.addPage('a4', 'landscape');
  drawProfileChart(doc, { points: chartPoints, labels: chartLabels });

  // Output
  const arrayBuffer = doc.output('arraybuffer');
  const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
  const filename = `livret_${sanitizeFilename(student.first_name)}_${sanitizeFilename(
    student.last_name
  )}_${btsCode}_${config.session_examen}.pdf`;

  return { blob, filename };
}
